import { useCallback } from "react";
import { useBuilderStore } from "../_stores/builder-store";
import { useBuilderToast } from "./useBuilderToast";
import { uploadComponentFile } from "../_logic/storage";
import type { ComponentImage } from "../_types";

/**
 * Returns a callback that uploads files and appends them as images to a component.
 * Used by canvas renderers (CarouselRenderer, VenueRenderer) when images are selected
 * from the empty-state file picker in ImageCarouselPreview.
 *
 * @param componentId - The component to add images to
 * @param currentImages - Current images array (used to append new ones)
 */
export function useCanvasImageUpload(
  componentId: string,
  currentImages: ComponentImage[] | undefined
): (files: File[]) => void {
  const pageId = useBuilderStore((s) => s.pageId);
  const updateComponent = useBuilderStore((s) => s.updateComponent);
  const { showToast } = useBuilderToast();

  return useCallback(
    (files: File[]) => {
      if (!pageId) return;

      const promises = files.map(async (file): Promise<ComponentImage | null> => {
        try {
          const url = await uploadComponentFile(pageId, componentId, file);
          const name = file.name.replace(/\.[^/.]+$/, "");
          return { id: crypto.randomUUID(), url, name };
        } catch {
          showToast(`Failed to upload: ${file.name}`, "error");
          return null;
        }
      });

      Promise.all(promises).then((results) => {
        const uploaded = results.filter((img): img is ComponentImage => img !== null);
        if (uploaded.length > 0) {
          updateComponent(componentId, { images: [...(currentImages ?? []), ...uploaded] });
        }
      });
    },
    [pageId, componentId, currentImages, updateComponent, showToast]
  );
}
